import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';

interface PrefetchOptions {
  router: AppRouterInstance;
  hrefs: string[];
  delay?: number;
  enableLogging?: boolean;
}

interface PrefetchResult {
  success: string[];
  failed: string[];
  total: number;
}

/**
 * Enhanced prefetch utility that works in both development and production
 * @param options - Configuration options for prefetching
 * @returns Promise with prefetch results
 */
export async function prefetchPages({
  router,
  hrefs,
  delay = 100,
  enableLogging = true
}: PrefetchOptions): Promise<PrefetchResult> {
  const validHrefs = hrefs.filter(href => 
    typeof href === 'string' && 
    href.startsWith('/') && 
    href !== '/'
  );

  if (validHrefs.length === 0) {
    if (enableLogging) {
      console.log('🚀 No valid routes to prefetch');
    }
    return { success: [], failed: [], total: 0 };
  }

  const result: PrefetchResult = {
    success: [],
    failed: [],
    total: validHrefs.length
  };

  // Add delay to ensure router is ready
  await new Promise(resolve => setTimeout(resolve, delay));

  const prefetchPromises = validHrefs.map(async (href) => {
    try {
      await router.prefetch(href);
      result.success.push(href);
      if (enableLogging) {
        console.log(`✅ Prefetched: ${href}`);
      }
    } catch (error) {
      result.failed.push(href);
      if (enableLogging) {
        console.warn(`⚠️ Failed to prefetch ${href}:`, error);
      }
    }
  });

  // Wait for all prefetch operations to complete
  await Promise.allSettled(prefetchPromises);

  if (enableLogging) {
    console.log(`🚀 Prefetching completed: ${result.success.length}/${result.total} successful`);
    if (result.failed.length > 0) {
      console.warn(`⚠️ Failed to prefetch ${result.failed.length} routes:`, result.failed);
    }
  }

  return result;
}

/**
 * Extract all hrefs from menu items (including sub-items)
 * @param menuItems - Array of menu items
 * @returns Array of unique hrefs
 */
export function extractHrefsFromMenuItems(menuItems: any[]): string[] {
  const hrefs = new Set<string>();
  
  menuItems.forEach((item) => {
    if (item.href) {
      hrefs.add(item.href);
    }
    if (item.subItems && Array.isArray(item.subItems)) {
      item.subItems.forEach((subItem: any) => {
        if (subItem.href) {
          hrefs.add(subItem.href);
        }
      });
    }
  });

  return Array.from(hrefs);
}

/**
 * Check if we're in development mode
 * @returns boolean indicating if we're in development
 */
export function isDevelopmentMode(): boolean {
  return process.env.NODE_ENV === 'development';
}

/**
 * Get prefetch status message
 * @param result - Prefetch result
 * @returns Status message
 */
export function getPrefetchStatusMessage(result: PrefetchResult): string {
  const { success, failed, total } = result;
  
  if (total === 0) {
    return 'No routes to prefetch';
  }
  
  if (failed.length === 0) {
    return `Successfully prefetched ${success.length} routes`;
  }
  
  if (success.length === 0) {
    return `Failed to prefetch all ${total} routes`;
  }
  
  return `Prefetched ${success.length}/${total} routes (${failed.length} failed)`;
}

/**
 * Development mode alternative: Preload critical resources
 * This helps improve perceived performance in development mode
 * @param hrefs - Array of routes to preload
 * @param enableLogging - Whether to enable logging
 */
export async function preloadCriticalResources(
  hrefs: string[], 
  enableLogging: boolean = true
): Promise<void> {
  if (!isDevelopmentMode()) {
    return;
  }

  const criticalRoutes = hrefs.filter(href => 
    href.includes('/dashboard') && 
    (href.includes('/dashboard') || href.includes('/settings') || href.includes('/profile'))
  );

  if (criticalRoutes.length === 0) {
    return;
  }

  if (enableLogging) {
    console.log('🔧 Development mode: Preloading critical resources...');
  }

  // Preload critical routes by making HEAD requests
  const preloadPromises = criticalRoutes.map(async (href) => {
    try {
      // Create a link element to preload the route
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      document.head.appendChild(link);
      
      // Clean up after a short delay
      setTimeout(() => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      }, 1000);

      if (enableLogging) {
        console.log(`🔧 Preloaded critical resource: ${href}`);
      }
    } catch (error) {
      if (enableLogging) {
        console.warn(`⚠️ Failed to preload ${href}:`, error);
      }
    }
  });

  await Promise.allSettled(preloadPromises);
  
  if (enableLogging) {
    console.log(`🔧 Preloaded ${criticalRoutes.length} critical resources`);
  }
}

/**
 * Enhanced prefetch function that includes development mode alternatives
 * @param options - Configuration options for prefetching
 * @returns Promise with prefetch results
 */
export async function enhancedPrefetchPages(options: PrefetchOptions): Promise<PrefetchResult> {
  const { router, hrefs, delay = 100, enableLogging = true } = options;
  
  // First, try the standard prefetch
  const result = await prefetchPages(options);
  
  // If we're in development mode and some prefetches failed, try alternative approach
  if (isDevelopmentMode() && result.failed.length > 0) {
    if (enableLogging) {
      console.log('🔧 Development mode: Trying alternative preloading...');
    }
    
    await preloadCriticalResources(result.failed, enableLogging);
  }
  
  return result;
}
